import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import getConfig from "next/config"
import { useRouter } from "next/router"
import { SetStateAction, SyntheticEvent, useEffect, useState } from "react"
import { t } from "translations"

import MUIAutocomplete from "@mui/material/Autocomplete"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled, useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"

import useDebounce from "@nhi/hooks/useDebounce"
import { normalizeQuery } from "@nhi/utils"

import Icon from "Components/Icon/Icon"

import { ISearchHeaderItem } from "./ISearchHeaderItem"
import useHeaderSearchData from "./useHeaderSearchData"

export default function HeaderSearch() {
    const [input, setInput] = useState("")
    const normalizedSuggestion = normalizeQuery(input, "suggestion")
    const [showPopout, setShowPopout] = useState(false)
    const [showSearch, setShowSearch] = useState(false)
    const [inputError, setInputError] = useState(false)
    const router = useRouter()
    const theme = useTheme()
    const isLg = useMediaQuery(theme.breakpoints.up("md"))
    const searchQueryFromRouter = (router.query.q as string)?.trim()
    const { publicRuntimeConfig } = getConfig()

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Delete" || e.key === "Escape") {
            setInput("")
            setInputError(false)
        }

        if (e.key === "Enter" && input.trim().length === 0) {
            e.preventDefault()
            setInputError(true)
        } else if (e.key === "Enter" && input.trim().length > 0) {
            setInputError(false)
            setShowPopout(false)
            router.push(
                `${publicRuntimeConfig.searchPath}?q=${encodeURIComponent(
                    input
                )}`
            )
        }

        if (e.key === "Tab") {
            setShowPopout(false)
            setInputError(false)
        }
    }

    useEffect(() => {
        if (showPopout) setInput(searchQueryFromRouter ?? "")
    }, [showPopout, searchQueryFromRouter, router])

    const debouncedQuery = useDebounce(normalizedSuggestion, 200)
    const headerSearchData = useHeaderSearchData(
        normalizedSuggestion,
        debouncedQuery
    )

    const onInputChange = (
        _event: SyntheticEvent<Element, Event>,
        value: string
    ) => {
        if (_event !== null) setInput(value)
        else setInput("")
    }

    const objectIsSearchLabel = (label: any) => {
        if (typeof label === "string" || label instanceof String) return false
        if (!label) return false
        return true
    }

    const onChange = (
        _event: SyntheticEvent<Element, Event>,
        value:
            | NonNullable<string | ISearchHeaderItem>
            | (string | ISearchHeaderItem)[]
            | null
    ) => {
        const isSearchLabel = objectIsSearchLabel(value)
        const searchLabel = isSearchLabel ? (value as ISearchHeaderItem) : null

        window.document.body.focus()
        if (searchLabel?.url) {
            setInput("")
            setShowPopout(false)
            setShowSearch(false)
            router.push(searchLabel.url)
        } else {
            setShowPopout(false)
            setShowSearch(false)
        }
    }

    if (isLg)
        return (
            <StyledWrapper>
                <Autocomplete
                    input={input}
                    onInputChange={onInputChange}
                    suggestions={headerSearchData.suggestions ?? []}
                    onChange={onChange}
                    setShowPopout={setShowPopout}
                    showPopout={showPopout}
                    objectIsSearchLabel={objectIsSearchLabel}
                    onKeyDown={onKeyDown}
                    headerSearchData={headerSearchData}
                    error={inputError}
                    onFocusInput={() => {
                        if (inputError) setInputError(false)
                    }}
                />
            </StyledWrapper>
        )

    return (
        <>
            <StyledIconButton
                onClick={() => setShowSearch(!showSearch)}
                aria-label={t.search.title}>
                {showSearch ? (
                    <Icon name="close" />
                ) : (
                    <StyledSearchIcon name="search" />
                )}
            </StyledIconButton>

            {showSearch && (
                <Autocomplete
                    input={input}
                    onInputChange={onInputChange}
                    suggestions={headerSearchData.suggestions ?? []}
                    onChange={onChange}
                    setShowPopout={setShowPopout}
                    showPopout={showPopout}
                    objectIsSearchLabel={objectIsSearchLabel}
                    onKeyDown={onKeyDown}
                    headerSearchData={headerSearchData}
                    error={inputError}
                    onFocusInput={() => {
                        if (inputError) setInputError(false)
                    }}
                />
            )}
        </>
    )
}

function Autocomplete({
    input,
    onInputChange,
    suggestions,
    showPopout,
    setShowPopout,
    onChange,
    objectIsSearchLabel,
    onKeyDown,
    headerSearchData,
    error,
    onFocusInput
}: {
    input: string
    onInputChange: (
        event: SyntheticEvent<Element, Event>,
        value: string
    ) => void
    suggestions: ISearchHeaderItem[]
    showPopout: boolean
    setShowPopout: (value: SetStateAction<boolean>) => void
    onChange: (
        event: SyntheticEvent<Element, Event>,
        value:
            | NonNullable<string | ISearchHeaderItem>
            | (string | ISearchHeaderItem)[]
            | null
    ) => void
    objectIsSearchLabel: (label: any) => boolean
    onKeyDown: (e: any) => void
    headerSearchData: any
    error?: boolean
    onFocusInput: () => void
}) {
    return (
        <StyledAutocomplete<ISearchHeaderItem, boolean, boolean, boolean>
            multiple={false}
            inputValue={input}
            filterOptions={options => options}
            onInputChange={onInputChange}
            options={suggestions}
            getOptionLabel={(option: ISearchHeaderItem | string) => {
                if (objectIsSearchLabel(option))
                    return (option as ISearchHeaderItem)?.label ?? input
                return option as string
            }}
            groupBy={(option: ISearchHeaderItem) => option.group}
            freeSolo
            disableClearable
            renderInput={params => (
                <>
                    <StyledInputBase
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            placeholder: t.search.title,
                            "aria-label": t.search.title
                        }}
                        InputLabelProps={{ disableAnimation: true }}
                        aria-hidden="false"
                        aria-invalid={!!error}
                        aria-errormessage={
                            error ? "autocomplete-error" : undefined
                        }
                        error={!!error}
                        onKeyDown={onKeyDown}
                        onClick={() => {
                            onFocusInput()
                        }}
                        title={
                            input.length === 0
                                ? t.search.searchButtonDeactivated
                                : undefined
                        }
                    />

                    {error && (
                        <StyledError role="alert">
                            {t.search.searchNoResult}
                        </StyledError>
                    )}

                    <StyledDesktopSearchIcon name="search" />

                    {input.length === 0 && (
                        <span
                            id="search-disabled-info"
                            className="sr-only"
                            aria-live="polite">
                            {t.search.searchButtonDeactivated}
                        </span>
                    )}
                    {headerSearchData.isLoading && input.length > 1 && (
                        <>
                            <StyledProgress
                                color="primary"
                                size={35}
                            />
                            <span
                                className="sr-only"
                                aria-live="polite">
                                {t.search.searchHitsLoading}
                            </span>
                        </>
                    )}
                </>
            )}
            renderGroup={props => (
                <li key={props.key}>
                    <GroupHeader variant="body1">{props.group}</GroupHeader>
                    <GroupItems>{props.children}</GroupItems>
                </li>
            )}
            renderOption={(props, option, { inputValue }) => {
                const matches = match(option.label, inputValue)
                const parts = parse(option.label, matches)

                const text = (
                    <StyledResult
                        variant="body2"
                        color="textPrimary">
                        {parts.map((part, index) => (
                            <StyledResultText
                                showhighlight={
                                    part.highlight ? "true" : "false"
                                }
                                // Letters in a search query can be the same, so we have to use index.
                                // eslint-disable-next-line react/no-array-index-key
                                key={part.text + index}>
                                {part.text}
                            </StyledResultText>
                        ))}
                    </StyledResult>
                )
                return (
                    <li
                        {...props}
                        color="inherit">
                        {text}
                    </li>
                )
            }}
            open={input.length > 0 && showPopout}
            onChange={(event, newValue) => {
                if (!error) onChange(event, newValue)
            }}
            clearOnBlur={true}
            onFocus={() => setShowPopout(true)}
            onBlur={() => setShowPopout(false)}
        />
    )
}

const StyledAutocomplete = styled(MUIAutocomplete)(({ theme }) => ({
    width: "100vw",
    top: theme.spacing(7),
    right: theme.spacing(-2),
    position: "absolute",
    backgroundColor: theme.palette.common.white,
    border: `${theme.spacing(0.5)} solid ${theme.palette.secondary.main}`,
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
        border: "none"
    },
    "&:focus-within": {
        [theme.breakpoints.up("sm")]: {
            outline: `${theme.spacing(0.5)} solid ${
                theme.palette.common.white
            }`,
            outlineOffset: theme.spacing(1)
        }
    },
    [theme.breakpoints.up("sm")]: {
        position: "absolute",
        top: theme.spacing(10),
        right: -28
    },
    [theme.breakpoints.up("md")]: {
        width: theme.spacing(38),
        height: theme.spacing(5),
        position: "static",
        display: "flex",
        alignItems: "center",
        border: "none"
    }
})) as typeof MUIAutocomplete

const StyledError = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[800]}`,
    position: "absolute",
    width: 200,
    padding: theme.spacing(1),
    marginLeft: theme.spacing(2),
    fontSize: "0.75rem",
    [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(20)
    }
}))

const GroupHeader = styled(Typography)(({ theme }) => ({
    position: "sticky",
    top: theme.spacing(-1),
    padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
    color: theme.palette.grey[600],
    backgroundColor: theme.palette.common.white
}))

const GroupItems = styled("ul")(({ theme }) => ({
    paddingLeft: theme.spacing(0)
}))

const StyledResult = styled(Typography)(({ theme }) => ({
    paddingLeft: theme.spacing(2)
}))

const StyledResultText = styled("span")<{ showhighlight: string }>(
    ({ theme, showhighlight }) => ({
        fontWeight: showhighlight === "true" ? 600 : 400,
        color:
            showhighlight === "true"
                ? theme.palette.text.primary
                : theme.palette.grey[800]
    })
)

const StyledWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    position: "relative",
    border: `${theme.spacing(0.3)} solid ${theme.palette.secondary.main}`,
    backgroundColor: theme.palette.common.white,
    height: theme.spacing(6)
}))

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.common.white,
    "& .MuiSvgIcon-root": {
        width: theme.spacing(4),
        height: theme.spacing(4),
        [theme.breakpoints.up("sm")]: {
            width: theme.spacing(6),
            height: theme.spacing(6)
        }
    }
}))

const StyledDesktopSearchIcon = styled(Icon)(({ theme }) => ({
    marginTop: theme.spacing(0),
    position: "static",
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
    stroke: theme.palette.primary.main,
    display: "none",
    [theme.breakpoints.up("md")]: {
        display: "block"
    },
    "& .MuiSvgIcon-root": {
        width: theme.spacing(5),
        height: theme.spacing(5)
    }
}))

const StyledSearchIcon = styled(Icon)(({ theme }) => ({
    marginTop: theme.spacing(0),
    position: "static",
    marginRight: theme.spacing(0),
    color: theme.palette.common.white,
    stroke: theme.palette.common.white,
    "& .MuiSvgIcon-root": {
        width: theme.spacing(3),
        height: theme.spacing(3)
    }
}))

const StyledProgress = styled(CircularProgress)(({ theme }) => ({
    position: "absolute",
    zIndex: 999,
    right: theme.spacing(9),
    top: theme.spacing(1.3),
    color: theme.palette.secondary.main,
    stroke: theme.palette.secondary.main,
    [theme.breakpoints.up("md")]: {
        top: theme.spacing(0.4),
        right: theme.spacing(6)
    }
}))

const StyledInputBase = styled(TextField)(({ theme }) => ({
    color: theme.palette.common.white,
    borderRadius: 0,
    paddingRight: 40,
    "& .MuiInputLabel-root": {
        color: theme.palette.grey[500],
        "&.MuiInputLabel-shrink": {
            display: "none"
        }
    },
    "& .MuiOutlinedInput-root": {
        height: "60%",
        width: "100%"
    },
    "& .MuiInputBase-input": {
        padding: theme.spacing(1, 4, 1, 3)
    },

    "& .MuiInputBase-input::placeholder": {
        color: theme.palette.primary.light,
        opacity: 0.9
    },
    "& .MuiInputBase-input::-webkit-input-placeholder": {
        color: theme.palette.primary.light,
        opacity: 0.9
    },
    "& .MuiInputBase-input::-moz-placeholder": {
        color: theme.palette.primary.light,
        opacity: 0.9
    },
    "& .MuiInputBase-input:-ms-input-placeholder": {
        color: theme.palette.primary.light,
        opacity: 0.9
    },
    "& .MuiInputBase-input::ms-input-placeholder": {
        color: theme.palette.primary.light,
        opacity: 0.9
    }
}))
